import React, { useCallback, useEffect, useState } from "react";
import ReactModal from "react-modal";
import Select from "react-select";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import moment from "moment-timezone";
import AsyncSelect from "react-select/async";
import NumberFormat from "react-number-format";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";

import { LoadingButton } from "../shared/loading-button";
import { CLIENT_PAYMENT_TYPE_OPTIONS, OPERATOR_PAYMENT_TYPE_OPTIONS } from "../../helpers/format-payment-type";
import TextAreaInput from "../textarea-input";
import ai from "../../helpers/axios";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import {
  formatOperatorOption,
  formatProspectOption,
  formatTripOption,
  formatContractOption,
  formatOFROption,
  formatGroupedOptionsByStage,
} from "./helpers";

momentTimezone.moment = moment;

const getOperators = async (token, params = {}) => {
  let { data } = await ai.auth(token).get("/api/opi/search", { params });
  return data?.operators ?? [];
};

const getOperatorOptions = (operators) => operators.map(formatOperatorOption);

const getProspects = async (token, params = {}) => {
  let { data } = await ai.auth(token).get("/api/prospects/search", { params });
  return data?.prospects ?? [];
};

const getProspectOptions = (prospects) => prospects.flatMap(({
  _id,
  firstAndLastName,
  lastName,
  firstName,
  emails,
  companyName,
  assigneeId,
  assigneeName,
}) => {
  if (!firstAndLastName && !lastName && !firstName) {
    return [];
  }

  return formatProspectOption({
    _id,
    firstAndLastName,
    lastName,
    firstName,
    emails,
    companyName,
    assigneeId,
    assigneeName,
  });
});

const getTrips = async (token, params = {}) => {
  let { data } = await ai.auth(token).get("/api/trips/search", { params });
  return { data: data?.data ?? [], totalLength: data?.countTotal ?? 0 };
};

const getTripOptions = (trips) => trips.map(formatTripOption);

const getContracts = async (token, params = {}) => {
  let { data } = await ai.auth(token).get("/api/trips/contracts/search", { params });
  return { data: data?.data ?? [], totalLength: data?.countTotal ?? 0 };
};

const getOFRs = async (token, params = {}) => {
  let { data } = await ai.auth(token).get("/api/trips/financials/search", { params });
  return { data: data?.data ?? [], totalLength: data?.countTotal ?? 0 };
};

const DEFAULT_OPTIONS_MAX_LIMIT = 50;

const PaymentLinkModal = ({
  payment,
  parentDispatch,
  values,
  showContractSelect,
  showOFRSelect,
  open,
  onRequestClose,
  ...props
}) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const getToken = useCallback(async () => getAccessTokenSilently().catch((e) => {
    console.error(e);
    return loginWithRedirect();
  }), [getAccessTokenSilently, loginWithRedirect]);

  const [isLoading, setIsLoading] = useState(false);
  const [tripDefaultOptions, setTripDefaultOptions] = useState([]);
  const [contractDefaultOptions, setContractDefaultOptions] = useState([]);
  const [ofrDefaultOptions, setOFRDefaultOptions] = useState([]);

  const getLoadOptionsFilters = useCallback((optionsType, searchString = "") => {
    const filters = {
      q: searchString,
    };

    if (["trip", "contract", "ofr"].includes(optionsType)) {
      filters.prospectFilter = values.selectedProspect?.value;
      filters.operatorFilter = values.selectedOperator?.value;
      filters.limit = DEFAULT_OPTIONS_MAX_LIMIT;
    }

    if (["contract", "ofr"].includes(optionsType)) {
      filters.tripFilter = values.selectedTrip?.value;
    }

    return filters;
  }, [values.selectedOperator?.value, values.selectedProspect?.value, values.selectedTrip?.value]);

  const loadOperators = useCallback(async (inputValue) => {
    const token = await getToken();

    const filters = getLoadOptionsFilters("operator", inputValue);

    const operators = await getOperators(token, filters);

    return getOperatorOptions(operators);
  }, [getLoadOptionsFilters, getToken]);

  const debouncedLoadOperators = useDebouncedCallback(
    (inputValue, callback) => {
      loadOperators(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadProspects = useCallback(async (inputValue) => {
    const token = await getToken();

    const filters = getLoadOptionsFilters("prospect", inputValue);

    const prospects = await getProspects(token, filters);

    return getProspectOptions(prospects);
  }, [getLoadOptionsFilters, getToken]);

  const debouncedLoadProspects = useDebouncedCallback(
    (inputValue, callback) => {
      loadProspects(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadTrips = useCallback(async (inputValue) => {
    const token = await getToken();

    const filters = getLoadOptionsFilters("trip", inputValue);

    const { data: trips } = await getTrips(token, filters);

    return getTripOptions(trips);
  }, [getLoadOptionsFilters, getToken]);

  const debouncedLoadTrips = useDebouncedCallback(
    (inputValue, callback) => {
      loadTrips(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadContracts = useCallback(async (inputValue) => {
    const token = await getToken();

    const filters = getLoadOptionsFilters("contract", inputValue);

    const { data: contracts } = await getContracts(token, filters);

    return formatGroupedOptionsByStage(contracts, formatContractOption, !values?.selectedTrip);
  }, [getLoadOptionsFilters, getToken, values?.selectedTrip]);

  const debouncedLoadContracts = useDebouncedCallback(
    (inputValue, callback) => {
      loadContracts(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadOFRs = useCallback(async (inputValue) => {
    const token = await getToken();

    const filters = getLoadOptionsFilters("ofr", inputValue);

    const { data: ofrs } = await getOFRs(token, filters);

    return formatGroupedOptionsByStage(ofrs, formatOFROption, !values?.selectedTrip);
  }, [getLoadOptionsFilters, getToken, values?.selectedTrip]);

  const debouncedLoadOFRs = useDebouncedCallback(
    (inputValue, callback) => {
      loadOFRs(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadTripsDefaultOptions = useCallback(async () => {
    setTripDefaultOptions([]);

    const token = await getToken();

    const filters = getLoadOptionsFilters("trip");

    const { data: trips } = await getTrips(token, filters);

    setTripDefaultOptions(getTripOptions(trips));
  }, [getLoadOptionsFilters, getToken]);

  const loadOFRsDefaultOptions = useCallback(async () => {
    setOFRDefaultOptions([]);

    const token = await getToken();

    const filters = getLoadOptionsFilters("ofr");

    const { data: ofrs } = await getOFRs(token, filters);

    setOFRDefaultOptions(formatGroupedOptionsByStage(ofrs, formatOFROption, !values?.selectedTrip));
  }, [getLoadOptionsFilters, getToken, values?.selectedTrip]);

  const loadContractsDefaultOptions = useCallback(async () => {
    setContractDefaultOptions([]);

    const token = await getToken();

    const filters = getLoadOptionsFilters("contract");

    const { data: contracts } = await getContracts(token, filters);

    setContractDefaultOptions(formatGroupedOptionsByStage(contracts, formatContractOption, !values?.selectedTrip));
  }, [getLoadOptionsFilters, getToken, values?.selectedTrip]);

  useEffect(() => {
    if (showOFRSelect && (!!values?.selectedOperator || values?.selectedTrip)) {
      loadOFRsDefaultOptions();
    }

    if (showContractSelect && (!!values?.selectedProspect || values?.selectedTrip)) {
      loadContractsDefaultOptions();
    }

    if (showContractSelect && !!values?.selectedProspect) {
      loadTripsDefaultOptions();
    }
  }, [
    showOFRSelect,
    showContractSelect,
    values?.selectedOperator,
    values?.selectedProspect,
    values?.selectedTrip,
    loadContractsDefaultOptions,
    loadOFRsDefaultOptions,
    loadTripsDefaultOptions,
  ]);

  const handleResetDependentFields = useCallback(() => {
    parentDispatch({
      type: "linkModalFieldChanged",
      field: "selectedTrip",
      value: null,
    });
    parentDispatch({
      type: "linkModalFieldChanged",
      field: "selectedContract",
      value: null,
    });
    parentDispatch({
      type: "linkModalFieldChanged",
      field: "selectedOFR",
      value: null,
    });
  }, [parentDispatch]);

  const handleSubmit = useCallback(async () => {
    if (
      (showContractSelect && !values?.selectedContract?.value) ||
      (showOFRSelect && !values?.selectedOFR?.value) ||
      !values?.paymentDate ||
      (!values?.amount && values.amount !== 0) ||
      !values?.paymentType?.value
    ) {
      return toast.error(
        "Please fill in all required fields and retry.",
      );
    }

    setIsLoading(true);

    try {
      const token = await getToken();

      const formData = {
        paymentDate: values?.paymentDate,
        paymentType: values?.paymentType?.value,
        amount: values?.amount,
        notes: values?.notes,
      };

      if (showContractSelect) {
        formData.contractId = values?.selectedContract?.value;
      }

      if (showOFRSelect) {
        formData.ofrId = values?.selectedOFR?.value;
      }

      const provider = payment?.objectType
        ? "stripe"
        : ["clients", "operators", "amex"].includes(payment.targetingPayments) ? "xero" : "plaid";
      const baseUrl = `/api/${provider}/transactions/${payment?._id}`;
      const linkUrl = payment?._lineItemID
        ? `${baseUrl}/line-items/${payment._lineItemID}/link`
        : `${baseUrl}/link`;
      await ai.auth(token).post(linkUrl, formData);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error linking transaction."));
    } finally {
      await onRequestClose();

      setIsLoading(false);
    }
  }, [getToken, payment?._id, payment?.targetingPayments, payment?.objectType, onRequestClose, values, showContractSelect, showOFRSelect, payment._lineItemID]);

  const handleValueChange = useCallback(({ field, value }) => {
    parentDispatch({
      type: "linkModalFieldChanged",
      field,
      value,
    });
  }, [parentDispatch]);

  return (
    <ReactModal
      isOpen={open}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      style={{
        overlay: {
          zIndex: 999999,
          backgroundColor: "rgb(32 53 68 / 50%)",
        },
        content: {
          width: "auto",
          border: "none",
          overflowY: "scroll",
          minWidth: "45vw",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      {...props}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header base-modal">
        <h4>Link Payment to {showContractSelect ? "a Contract" : "an OFR"}</h4>
      </div>

      <div className="modal-body base-modal modal-body-with-scroll">

      </div>
      <div className="modal-body base-modal overflow-scroll">
        <form id="link-payment" className="w-100 base-form mt-20" onSubmit={handleSubmit}>
          {showOFRSelect && (
            <div className="form-group">
              <label htmlFor="selectedOperator" className="base-label">
                Filter by Operator
              </label>

              <AsyncSelect
                className="selectedOperator-select"
                classNamePrefix="select"
                loadOptions={debouncedLoadOperators}
                placeholder="Search/Select Operator"
                onChange={(selectedValue) => {
                  handleValueChange({
                    field: "selectedOperator",
                    value: selectedValue,
                  });
                  handleResetDependentFields();
                }}
                key="key-selected-operator"
                value={values.selectedOperator}
                name="selectedOperator"
                isClearable
              />
            </div>
          )}

          {showContractSelect && (
            <div className="form-group">
              <label htmlFor="selectedProspect" className="base-label">
                Filter by Prospect
              </label>

              <AsyncSelect
                className="selectedProspect-select"
                classNamePrefix="select"
                loadOptions={debouncedLoadProspects}
                placeholder="Search/Select Prospect"
                onChange={(selectedValue) => {
                  handleValueChange({
                    field: "selectedProspect",
                    value: selectedValue,
                  });
                  handleResetDependentFields();
                }}
                key="key-selected-prospect"
                value={values.selectedProspect}
                name="selectedProspect"
                isClearable
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="selectedTrip" className="base-label">
              Filter by Trip
            </label>

            <AsyncSelect
              className="selectedTrip-select"
              classNamePrefix="select"
              loadOptions={debouncedLoadTrips}
              defaultOptions={tripDefaultOptions}
              placeholder="Search/Select Trip"
              onChange={(selectedValue) => {
                handleValueChange({
                  field: "selectedTrip",
                  value: selectedValue,
                });
              }}
              key="key-selected-trip"
              value={values.selectedTrip}
              name="selectedTrip"
              isClearable
            />
          </div>

          {showContractSelect && (
            <div className="form-group">
              <label htmlFor="selectedContract" className="base-label">
                Contract <span className="required">*</span>
              </label>

              <AsyncSelect
                className="selectedContract-select"
                classNamePrefix="select"
                loadOptions={debouncedLoadContracts}
                defaultOptions={contractDefaultOptions}
                placeholder="Contract"
                onChange={(selectedValue) => {
                  handleValueChange({
                    field: "selectedContract",
                    value: selectedValue,
                  });
                }}
                key="key-selected-contract"
                value={values.selectedContract}
                name="selectedContract"
              />
            </div>
          )}

          {showOFRSelect && (
            <div className="form-group">
              <label htmlFor="selectedOFR" className="base-label">
                OFR <span className="required">*</span>
              </label>

              <AsyncSelect
                className="selectedOFR-select"
                classNamePrefix="select"
                loadOptions={debouncedLoadOFRs}
                defaultOptions={ofrDefaultOptions}
                placeholder="OFR"
                onChange={(selectedValue) => {
                  handleValueChange({
                    field: "selectedOFR",
                    value: selectedValue,
                  });
                }}
                key="key-selected-ofr"
                value={values.selectedOFR}
                name="selectedOFR"
              />
            </div>
          )}

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-half">
              <label htmlFor={`paymentType`} className="base-label">
                Payment Type <span className="required">*</span>
              </label>

              <Select
                className="paymentType-select"
                classNamePrefix="select"
                options={showOFRSelect ? OPERATOR_PAYMENT_TYPE_OPTIONS : CLIENT_PAYMENT_TYPE_OPTIONS}
                placeholder="Payment Type"
                onChange={(selectedValue) => {
                  handleValueChange({
                    field: "paymentType",
                    value: selectedValue,
                  });
                }}
                key={`key-payment-type`}
                value={values.paymentType}
                name="paymentType"
              />
            </div>

            <div className="datepickerContainer mb-15 flex-half">
              <label htmlFor={`date`} className="base-label">
                Date <span className="required">*</span>
              </label>

              <Datepicker
                value={values.paymentDate}
                onChange={(event) => {
                  handleValueChange({
                    field: "paymentDate",
                    value: event.value,
                  });
                }}
                dataTimezone="utc"
                displayTimezone="local"
                timezonePlugin={momentTimezone}
                controls={["date"]}
                disabled
              />
            </div>
          </div>

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-half mb-0">
              <label htmlFor={`transactionType`} className="base-label">
                Transaction Type <span className="required">*</span>
              </label>

              <Select
                className="transactionType-select"
                classNamePrefix="select"
                placeholder="Transaction Type"
                key={`key-transaction-type`}
                value={values.transactionType}
                name="transactionType"
                isDisabled
              />
            </div>

            <div className="form-group flex-half mb-0">
              <label htmlFor={`amount`} className="base-label">
                Amount <span className="required">*</span>
              </label>

              <NumberFormat
                value={values.amount}
                thousandSeparator={true}
                prefix={"$"}
                placeholder="Amount"
                className={`semibold-text amount-${values.amount >= 0 ? "positive" : "negative"}`}
                decimalScale={2}
                name="amount"
                allowNegative={values.transactionType?.value === "spent"}
                disabled
              />
            </div>
          </div>

          <TextAreaInput
            label="Notes"
            name="notes"
            containerClasses="mb-0"
            value={values.notes}
            handleChange={(e) => {
              handleValueChange({
                field: "notes",
                value: e.target.value,
              });
            }}
          />
        </form>
      </div>

      <div className="modal-footer no-border w-100 d-flex gap-10">
        <LoadingButton
          className="base-button evo-blue flex-half"
          onClick={handleSubmit}
          isLoading={isLoading}
        >
          Submit
        </LoadingButton>

        <button
          className="base-button btn-danger flex-half"
          onClick={() => onRequestClose(false)}
          disabled={isLoading}
        >
          Cancel
        </button>
      </div>
    </ReactModal>
  );
};

export default PaymentLinkModal;
